import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  HMIComputedAggregate,
  HMIComputedFormula,
  HMIComputedOperand,
  HMIComputedOperator,
  HMIField,
} from '../../types';

export type ComputedFormulaDraft = Omit<HMIComputedFormula, 'id'>;

export interface ComputedFieldEditorDialogProps {
  /** Present when editing an existing formula; absent when adding a new one. */
  formula?: HMIComputedFormula;
  /** Every field in the template. */
  fields: HMIField[];
  onCancel: () => void;
  onSave: (draft: ComputedFormulaDraft) => void;
  onDelete?: () => void;
}

const AGGREGATE_OPTIONS: { value: HMIComputedAggregate; label: string }[] = [
  { value: 'gridMaxRowOnCount', label: 'Highest number of ON cells in any grid row' },
];

const OPERATOR_OPTIONS: { value: HMIComputedOperator; label: string }[] = [
  { value: '+', label: '+ (add)' },
  { value: '-', label: '- (subtract)' },
  { value: '*', label: '\u00d7 (multiply)' },
  { value: '/', label: '\u00f7 (divide)' },
];

function fieldById(fields: HMIField[], id: string): HMIField | undefined {
  return fields.find((f) => f.id === id);
}

function defaultOperand(fields: HMIField[], gridFields: HMIField[]): HMIComputedOperand {
  if (gridFields[0]) return { kind: 'aggregate', fn: 'gridMaxRowOnCount', fieldId: gridFields[0].id };
  if (fields[0]) return { kind: 'field', fieldId: fields[0].id };
  return { kind: 'literal', value: 0 };
}

interface OperandEditorProps {
  label: string;
  operand: HMIComputedOperand;
  fields: HMIField[];
  gridFields: HMIField[];
  onChange: (next: HMIComputedOperand) => void;
}

/** Editor for one side of a formula: a field's live value, a fixed number, or a built-in aggregate over a grid field. */
function OperandEditor({ label, operand, fields, gridFields, onChange }: OperandEditorProps) {
  const handleKindChange = (kind: HMIComputedOperand['kind']) => {
    if (kind === 'literal') onChange({ kind: 'literal', value: 0 });
    else if (kind === 'field') onChange({ kind: 'field', fieldId: fields[0]?.id ?? '' });
    else onChange({ kind: 'aggregate', fn: 'gridMaxRowOnCount', fieldId: gridFields[0]?.id ?? '' });
  };

  return (
    <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
      <Grid item xs={12} sm={3}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          select
          fullWidth
          size="small"
          label="Type"
          value={operand.kind}
          onChange={(e) => handleKindChange(e.target.value as HMIComputedOperand['kind'])}
        >
          <MenuItem value="field">Another field's value</MenuItem>
          <MenuItem value="literal">A fixed number</MenuItem>
          <MenuItem value="aggregate" disabled={gridFields.length === 0}>
            Grid aggregate
          </MenuItem>
        </TextField>
      </Grid>
      <Grid item xs={12} sm={6}>
        {operand.kind === 'field' && (
          <TextField
            select
            fullWidth
            size="small"
            label="Field"
            value={operand.fieldId}
            onChange={(e) => onChange({ kind: 'field', fieldId: e.target.value })}
          >
            {fields.map((f) => (
              <MenuItem key={f.id} value={f.id}>
                {f.caption}
              </MenuItem>
            ))}
          </TextField>
        )}
        {operand.kind === 'literal' && (
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Value"
            value={operand.value}
            onChange={(e) => onChange({ kind: 'literal', value: Number(e.target.value) })}
          />
        )}
        {operand.kind === 'aggregate' && (
          <Stack direction="row" spacing={1}>
            <TextField
              select
              fullWidth
              size="small"
              label="Aggregate"
              value={operand.fn}
              onChange={(e) =>
                onChange({ kind: 'aggregate', fn: e.target.value as HMIComputedAggregate, fieldId: operand.fieldId })
              }
            >
              {AGGREGATE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              fullWidth
              size="small"
              label="Grid field"
              value={operand.fieldId}
              onChange={(e) => onChange({ kind: 'aggregate', fn: operand.fn, fieldId: e.target.value })}
            >
              {gridFields.map((f) => (
                <MenuItem key={f.id} value={f.id}>
                  {f.caption}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        )}
      </Grid>
    </Grid>
  );
}

/**
 * Add / edit / delete form for one `HMIComputedFormula` - a field whose
 * value is calculated client-side from other fields (e.g. "Heating Time" =
 * highest number of ON lamp cells in any grid row, multiplied by "Time per
 * Division"). Never touches the recipe/device - purely a UI-side formula
 * re-evaluated on every poll tick and field commit.
 */
function ComputedFieldEditorDialog({ formula, fields, onCancel, onSave, onDelete }: ComputedFieldEditorDialogProps) {
  const isEditing = Boolean(formula);
  const gridFields = fields.filter((f) => f.datatype === 'array');
  // Any field can technically be the target, but fields already marked "derived" are the intended use case.
  const derivedFields = fields.filter((f) => f.access === 'derived' || f.sourceKind === 'derived');
  const targetCandidates = derivedFields.length > 0 ? derivedFields : fields;

  const [targetFieldId, setTargetFieldId] = useState(formula?.targetFieldId ?? targetCandidates[0]?.id ?? '');
  const [left, setLeft] = useState<HMIComputedOperand>(formula?.left ?? defaultOperand(fields, gridFields));
  const [operator, setOperator] = useState<HMIComputedOperator>(formula?.operator ?? '*');
  const [right, setRight] = useState<HMIComputedOperand>(formula?.right ?? { kind: 'field', fieldId: fields[0]?.id ?? '' });
  const [decimals, setDecimals] = useState<string>(
    formula?.decimals !== undefined ? String(formula.decimals) : ''
  );

  const targetField = fieldById(fields, targetFieldId);
  const isValid =
    Boolean(targetFieldId) &&
    (left.kind !== 'field' || Boolean(left.fieldId)) &&
    (left.kind !== 'aggregate' || Boolean(left.fieldId)) &&
    (right.kind !== 'field' || Boolean(right.fieldId)) &&
    (right.kind !== 'aggregate' || Boolean(right.fieldId));

  const handleSave = () => {
    if (!isValid) return;
    onSave({
      targetFieldId,
      left,
      operator,
      right,
      decimals: decimals.trim() === '' ? undefined : Number(decimals),
    });
  };

  if (fields.length === 0) {
    return (
      <Dialog open onClose={onCancel} fullWidth maxWidth="sm">
        <DialogTitle>Add computed field</DialogTitle>
        <DialogContent>
          <Alert severity="info">Add at least one field before defining a computed formula.</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open onClose={onCancel} fullWidth maxWidth="md">
      <DialogTitle>{isEditing ? 'Edit computed field' : 'Add computed field'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Calculates a field's value in the UI from other fields on every value change - it never
            comes from the recipe file or the device. Mark the target field's "Access" as{' '}
            <strong>Derived (computed)</strong> on the Layout tab so the operator can&apos;t edit it directly.
          </Typography>

          {derivedFields.length === 0 && (
            <Alert severity="warning">
              No field is currently marked <strong>Derived</strong>. You can still pick any field below, but
              consider changing the target field's Access/Source to Derived on the Layout tab first.
            </Alert>
          )}

          <TextField
            select
            fullWidth
            size="small"
            label="Target field (the one this formula calculates)"
            value={targetFieldId}
            onChange={(e) => setTargetFieldId(e.target.value)}
          >
            {targetCandidates.map((f) => (
              <MenuItem key={f.id} value={f.id}>
                {f.caption}
              </MenuItem>
            ))}
          </TextField>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Formula
            </Typography>
            <OperandEditor label="Left side" operand={left} fields={fields} gridFields={gridFields} onChange={setLeft} />
            <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Grid item xs={12} sm={3}>
                <Typography variant="caption" color="text.secondary">
                  Operator
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={operator}
                  onChange={(e) => setOperator(e.target.value as HMIComputedOperator)}
                >
                  {OPERATOR_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
            <OperandEditor label="Right side" operand={right} fields={fields} gridFields={gridFields} onChange={setRight} />
          </Box>

          <TextField
            fullWidth
            size="small"
            type="number"
            label="Decimal places (optional)"
            placeholder={targetField?.decimals !== undefined ? `Defaults to ${targetField.decimals}` : 'Defaults to 0'}
            value={decimals}
            onChange={(e) => setDecimals(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
        <Box>
          {isEditing && onDelete && (
            <Button color="error" startIcon={<DeleteIcon />} onClick={onDelete}>
              Delete
            </Button>
          )}
        </Box>
        <Stack direction="row" spacing={1}>
          <Button onClick={onCancel}>Cancel</Button>
          <Button variant="contained" disabled={!isValid} onClick={handleSave}>
            Save
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

export default ComputedFieldEditorDialog;
